import React from 'react';
import { injectIntl, InjectedIntlProps } from 'react-intl';
import {
  withStyles, Theme, WithStyles, createStyles,
} from '@material-ui/core/styles/index';
import Avatar from '@material-ui/core/Avatar/index';
import {
  DialogTitle,
  Tab,
  Tabs,
  Typography,
  DialogContent,
  DialogActions,
  Divider,
} from '@material-ui/core';
import classNames from 'classnames';
// @ts-ignore
import Timestamp from 'react-timestamp';

// @ts-ignore
import CancelButton from 'hcla-web-frontend-primitives/app/components/primitives/Buttons/CancelButton';

import QRChip from '../../../../Primitives/QRchip';
import TreeSnapshotView from './TreeSnapshotView';
import QuantificationConfig from './QuantificationConfig';
import { QuantificationResultJSON } from '../../../Quantification/QuantificationResultJSON';
import RiskCurvesContent from '../../../../Generic/RiskCurvesContent';
import { TreeContext } from '../../TreeContext';
import { computeAssetAggregationData, constructLineAggregationData } from '../../../../Generic/RiskCurveHelper';

const styles = (theme: Theme) => createStyles({
  darker: {
    background: theme.palette.type === 'dark' ? 'rgba(60,60,60,0.2)' : 'rgba(120,120,120,0.08)',
  },
  background: {
    minHeight: '40vh',
    padding: 'unset',
    display: 'flex',
    flexDirection: 'column',
  },
  titleRoot: {
    display: 'flex',
    alignItems: 'center',
    '& > *': {
      display: 'flex',
    },
  },
  text: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexGrow: 1,
  },
  left: {},
  right: {},
  titleText: {
    margin: `0px ${theme.spacing(3)}px`,
  },
  plotContainer: {
    width: 'max-content',
  },
});

const quantifiedText = 'Quantified on ';

interface Props extends WithStyles<typeof styles>, InjectedIntlProps {
  detail: QuantificationResultJSON;
  treeTitle: string;
  cutsetView: string | JSX.Element;
  measureView: JSX.Element;
  requestClose(e: React.SyntheticEvent<Element, Event>): void;
  failureProbability?: number;
  riskCurves?: {[key: string]: any};
  contentOnly?: boolean;
  lineLevelRiskCurves?: any;
}

interface State {
  modalIdx: number;
  assetRiskCurveAggregatedData: any;
  lineLevelRiskCurveAggregatedData: any;
}

export const QuantificationResultContext = React.createContext({});

export class QuantificationResultModal extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      modalIdx: 0,
      assetRiskCurveAggregatedData: null,
      lineLevelRiskCurveAggregatedData: null,
    };
  }

  componentDidMount() {
    const { detail, riskCurves, lineLevelRiskCurves } = this.props;
    if (riskCurves && detail) this.setState({ assetRiskCurveAggregatedData: computeAssetAggregationData(riskCurves, detail) });
    if (lineLevelRiskCurves && detail) this.setState({ lineLevelRiskCurveAggregatedData: constructLineAggregationData(lineLevelRiskCurves.map(asset => computeAssetAggregationData(asset.risk_curves, detail))) });
  }

  render () {
    const { modalIdx, assetRiskCurveAggregatedData, lineLevelRiskCurveAggregatedData } = this.state;
    const {
      detail, treeTitle, requestClose, classes, intl, cutsetView, measureView, failureProbability, contentOnly, riskCurves,
    } = this.props;

    const showAssetRiskCurveTab = !!(assetRiskCurveAggregatedData && !lineLevelRiskCurveAggregatedData);
    const showLineLevelRiskCurveTab = !!(!assetRiskCurveAggregatedData && lineLevelRiskCurveAggregatedData);
    const showAggregatedRiskCurveTab = !!(assetRiskCurveAggregatedData || lineLevelRiskCurveAggregatedData);

    return (
      <React.Fragment>
        <QuantificationResultContext.Provider value={{ detail }}>
          <DialogTitle className={classes.titleRoot} disableTypography>
            <Avatar>{detail.id}</Avatar>
            <div className={classes.text}>
              <div className={classes.left}>
                <Typography variant="h5" color="textPrimary" className={classes.titleText}>{treeTitle}</Typography>
                <Typography variant="subtitle1" color="textSecondary" className={classes.titleText}>
                  {quantifiedText}
                  <Timestamp
                    date={detail.date_created}
                    options={{ includeDay: true, twentyFourHour: true }}
                  />
                </Typography>
              </div>
              <div className={classes.right}>
                {failureProbability && <QRChip label={failureProbability} />}
              </div>
            </div>
          </DialogTitle>
          <DialogContent className={classNames([classes.background, classes.darker])}>
            <Divider light />
            <Tabs
              value={modalIdx}
              indicatorColor="primary"
              textColor="secondary"
              centered
              variant="fullWidth"
            >
              <Tab label="Minimal Cutsets" onClick={e => this.setState({ modalIdx: 0 })} />
              <Tab label="Importance Measures" onClick={e => this.setState({ modalIdx: 1 })} />
              <Tab label="Configuration" onClick={e => this.setState({ modalIdx: 2 })} />
              <Tab label="Tree Snapshot" onClick={e => this.setState({ modalIdx: 3 })} />
              {showAggregatedRiskCurveTab && <Tab label="Aggregated Risk Curves" onClick={e => this.setState({ modalIdx: 4 })} />}
            </Tabs>
            <div className={classes.darker}>
              {/*
              // @ts-ignore */}
              { modalIdx === 0 && { ...cutsetView }}
              { modalIdx === 1 && { ...measureView }}
              { modalIdx === 2 && <QuantificationConfig configuration={detail.configuration} />}
              { modalIdx === 3 && <TreeContext.Consumer>{ value => <TreeSnapshotView treeId={value.treeId} /> }</TreeContext.Consumer>}
              { showAssetRiskCurveTab && modalIdx === 4 && (
                <RiskCurvesContent
                  type={"assets"}
                  acresXY={assetRiskCurveAggregatedData.acresXY}
                  buildingImpactedXY={assetRiskCurveAggregatedData.buildingImpactedXY}
                  populationImpactedXY={assetRiskCurveAggregatedData.populationImpactedXY}
                  combinedImpactXY={assetRiskCurveAggregatedData.combinedImpactXY}
                  acresNoPSPSXY={assetRiskCurveAggregatedData.acresNoPSPSXY}
                  acresPSPSXY={assetRiskCurveAggregatedData.acresPSPSXY}
                  buildingImpactedNoPSPSXY={assetRiskCurveAggregatedData.buildingImpactedNoPSPSXY}
                  buildingImpactedPSPSXY={assetRiskCurveAggregatedData.buildingImpactedPSPSXY}
                  populationImpactedNoPSPSXY={assetRiskCurveAggregatedData.populationImpactedNoPSPSXY}
                  populationImpactedPSPSXY={assetRiskCurveAggregatedData.populationImpactedPSPSXY}
                  combinedImpactNoPSPSXY={assetRiskCurveAggregatedData.combinedImpactNoPSPSXY}
                  combinedImpactPSPSXY={assetRiskCurveAggregatedData.combinedImpactPSPSXY}
                  withAcceptanceLines
                  hideBaseCurves
                />
              )}
              { showLineLevelRiskCurveTab && modalIdx === 4 && (
                <RiskCurvesContent
                  type={"tline"}
                  acresXY={lineLevelRiskCurveAggregatedData.acresXY}
                  acresNoPSPSXY={lineLevelRiskCurveAggregatedData.acresNoPSPSXY}
                  acresPSPSXY={lineLevelRiskCurveAggregatedData.acresPSPSXY}
                  buildingImpactedXY={lineLevelRiskCurveAggregatedData.buildingImpactedXY}
                  buildingImpactedNoPSPSXY={lineLevelRiskCurveAggregatedData.buildingImpactedNoPSPSXY}
                  buildingImpactedPSPSXY={lineLevelRiskCurveAggregatedData.populationImpactedPSPSXY}
                  populationImpactedXY={lineLevelRiskCurveAggregatedData.populationImpactedXY}
                  populationImpactedNoPSPSXY={lineLevelRiskCurveAggregatedData.populationImpactedNoPSPSXY}
                  populationImpactedPSPSXY={lineLevelRiskCurveAggregatedData.populationImpactedPSPSXY}
                  withAcceptanceLines
                  hideBaseCurves
                />
              )}
            </div>
          </DialogContent>
          {!contentOnly
          && (
            <DialogActions>
              <CancelButton
                variant="outlined"
                size="large"
                onClick={requestClose}
              >
                {intl.formatMessage({ id: 'Close' })}
              </CancelButton>
            </DialogActions>
          )
          }
        </QuantificationResultContext.Provider>
      </React.Fragment>
    );
  }
}

export default withStyles(styles)(injectIntl(QuantificationResultModal));
